import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';

/**
 * Pagination component — always shows page-size selector, result info, and page numbers.
 *
 * Props:
 *   currentPage       – 1-based current page
 *   totalPages        – total number of pages
 *   totalResults      – total record count (for "X–Y of Z" display)
 *   perPage           – current page size
 *   onPageChange      – (page: number) => void
 *   onPageSizeChange  – (size: number) => void   (omit to hide selector)
 *   pageSizeOptions   – array of allowed sizes   (default [20, 50, 100, 200])
 *   disabled          – disables all controls
 *   className         – extra wrapper class
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalResults,
  perPage,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100, 200],
  disabled = false,
  className = '',
  // ── Back-compat aliases for older call sites (do not remove) ──
  // Several pages predate the current API and pass these legacy names. Without
  // them, `totalPages` is undefined → `Math.max(1, undefined)` = NaN (the bug
  // that showed a "NaN" page button). New props always take precedence.
  totalPagesCount,
  totalRows,
  pageSize,
  onChange,
}) => {
  const rTotalPages   = totalPages   ?? totalPagesCount ?? 1;
  const rTotalResults = totalResults ?? totalRows       ?? 0;
  const rPerPage      = perPage      ?? pageSize         ?? 20;
  // Legacy onChange has signature (page, pageSize); the new API is onPageChange(page).
  const rOnPageChange     = onPageChange || (onChange ? (p) => onChange(p, rPerPage) : (() => {}));
  const rOnPageSizeChange = onPageSizeChange || (onChange ? (s) => onChange(1, s) : undefined);

  const safeTotal = Math.max(1, Number(rTotalPages) || 1);

  const startResult = rTotalResults > 0 ? (currentPage - 1) * rPerPage + 1 : 0;
  const endResult   = rTotalResults > 0 ? Math.min(currentPage * rPerPage, rTotalResults) : 0;

  // Page number list with ellipsis — always show first, last, and ±1 around current
  const pageNumbers = useMemo(() => {
    if (safeTotal <= 1) return [1];

    const pages = new Set([1, safeTotal]);
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(safeTotal - 1, currentPage + 1); i++) {
      pages.add(i);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p - prev > 1) result.push('…');
      result.push(p);
      prev = p;
    }
    return result;
  }, [currentPage, safeTotal]);

  const go = (p) => {
    if (!disabled && p >= 1 && p <= safeTotal) rOnPageChange(p);
  };

  const btnBase =
    'tw-inline-flex tw-items-center tw-justify-center tw-min-w-[32px] tw-h-8 tw-px-2 tw-text-sm tw-border tw-rounded-md tw-transition-colors tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-not-allowed';
  const btnNormal = `${btnBase} tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-bg-slate-100`;
  const btnActive = `${btnBase} tw-bg-blue-600 tw-border-blue-600 tw-text-white tw-font-semibold`;

  return (
    <div className={`tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap ${className}`}>

      {/* Left — page-size selector + result info */}
      <div className="tw-flex tw-items-center tw-gap-3">
        {rOnPageSizeChange && (
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <label className="tw-text-xs tw-text-slate-500 tw-whitespace-nowrap tw-leading-none tw-self-center">Rows:</label>
            <select
              value={rPerPage}
              onChange={(e) => {
                const s = Number(e.target.value);
                if (onPageSizeChange) { onPageSizeChange(s); rOnPageChange(1); }
                else { rOnPageSizeChange(s); }
              }}
              disabled={disabled}
              className="tw-border tw-border-slate-300 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs tw-bg-white tw-text-slate-700 hover:tw-border-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors tw-cursor-pointer"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        )}

        {rTotalResults > 0 && (
          <span className="tw-text-xs tw-text-slate-500 tw-whitespace-nowrap">
            <span className="tw-font-semibold tw-text-slate-700">{startResult.toLocaleString()}</span>
            {' – '}
            <span className="tw-font-semibold tw-text-slate-700">{endResult.toLocaleString()}</span>
            {' of '}
            <span className="tw-font-semibold tw-text-slate-700">{rTotalResults.toLocaleString()}</span>
          </span>
        )}
      </div>

      {/* Right — navigation */}
      <div className="tw-flex tw-items-center tw-gap-1">
        {/* First */}
        <button
          onClick={() => go(1)}
          disabled={currentPage <= 1 || disabled}
          className={btnNormal}
          aria-label="First page"
          title="First page"
        >
          <FaAngleDoubleLeft size={11} />
        </button>

        {/* Previous */}
        <button
          onClick={() => go(currentPage - 1)}
          disabled={currentPage <= 1 || disabled}
          className={btnNormal}
          aria-label="Previous page"
        >
          <FaChevronLeft size={11} />
        </button>

        {/* Page numbers */}
        {pageNumbers.map((p, idx) =>
          p === '…' ? (
            <span key={`ellipsis-${idx}`} className="tw-px-1 tw-text-slate-400 tw-text-sm tw-select-none">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => go(p)}
              disabled={disabled}
              className={p === currentPage ? btnActive : btnNormal}
              aria-label={`Page ${p}`}
              aria-current={p === currentPage ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => go(currentPage + 1)}
          disabled={currentPage >= safeTotal || disabled}
          className={btnNormal}
          aria-label="Next page"
        >
          <FaChevronRight size={11} />
        </button>

        {/* Last */}
        <button
          onClick={() => go(safeTotal)}
          disabled={currentPage >= safeTotal || disabled}
          className={btnNormal}
          aria-label="Last page"
          title="Last page"
        >
          <FaAngleDoubleRight size={11} />
        </button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  currentPage:      PropTypes.number.isRequired,
  // Not required: legacy call sites supply the aliases below instead.
  totalPages:       PropTypes.number,
  onPageChange:     PropTypes.func,
  onPageSizeChange: PropTypes.func,
  totalResults:     PropTypes.number,
  perPage:          PropTypes.number,
  pageSizeOptions:  PropTypes.arrayOf(PropTypes.number),
  disabled:         PropTypes.bool,
  className:        PropTypes.string,
  // Back-compat aliases
  totalPagesCount:  PropTypes.number,
  totalRows:        PropTypes.number,
  pageSize:         PropTypes.number,
  onChange:         PropTypes.func,
};

export default Pagination;
